import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import { PageSection, Title, Form, FormGroup, Grid, GridItem, TextInput, Button, Alert, DataList, DataListItem, DataListItemRow, DataListItemCells, DataListCell } from '@patternfly/react-core';
import UserModel from '@app/Model/UserModel';
import PageLoader from '@app/Page/PageLoader';
import axios from "@app/Security/AxiosAuthentication";
import ApiEndpoints from '@app/utils/ApiEndpoints';
import htmlParser from '@app/utils/htmlParser';
import RollbarHelper from '@app/utils/RollbarHelper';
import moment from 'moment-timezone';

interface AnnualMeetingRegPageProps {
    pageData: PageModel;
    currentUser: UserModel;
}

interface AnnualMeetingRegistration {
    id?: number;
    firstName: string;
    lastName: string;
    spouse: string;
    company: string;
    fpuName: string;
    fieldManager: string;
    contactNumber: string;
    email: string;
    // Middleware requires a string instead of a number
    numberAttendingDinner: string;
    numberAttendingMeeting: string;
    registeringUser?: number;
    meetingDate?: string;
    title?: string;
}

const AnnualMeetingRegPage: React.FC<AnnualMeetingRegPageProps> = ({ pageData, currentUser, children }) => {
    const [validated, setValidated] = React.useState<'success'|'warning'|'error'|'default'>('default');
    const [loading, setLoading] = React.useState(true);
    const [previousSubmissionId, setPreviousSubmissionId] = React.useState<number|null>(null);
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [spouseName, setSpouseName] = React.useState('');
    const [companyName, setCompanyName] = React.useState('');
    const [fpu, setFpu] = React.useState('');
    const [fieldManager, setFieldManager] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [attendees, setAttendees] = React.useState('0');
    const [attendees2, setAttendees2] = React.useState('0');
    const [submitMessage, setSubmitMessage] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [submitMessageVariant, setSubmitMessageVariant] = React.useState<'success'|'warning'|'default'|'danger'|'info'|undefined>('default');

    // Get previous submission if applicable
    React.useEffect(() => {
        const controller = new AbortController();
        const year = moment().format('YYYY');
        const baseUrl: string = process.env.API_BASEURL ?? '';
        const url = `${baseUrl}${ApiEndpoints.ANNUAL_MEETING_REG}?meeting_year=${year}&user=${currentUser.id}`;

        let mounted = true;

        axios.get(
            url,
            {
                withCredentials: true,
                headers: {
                    "Accept": "application/json"
                }
            }
        )
            .then(response => {
                const submissions: AnnualMeetingRegistration[] = response.data;
                if (mounted && submissions.length > 0) {
                    setFirstName(submissions[0].firstName);
                    setLastName(submissions[0].lastName);
                    setSpouseName(submissions[0].spouse);
                    setCompanyName(submissions[0].company);
                    setFpu(submissions[0].fpuName);
                    setFieldManager(submissions[0].fieldManager);
                    setPhone(submissions[0].contactNumber);
                    setEmail(submissions[0].email);
                    setAttendees(`${submissions[0].numberAttendingMeeting}`);
                    setAttendees2(`${submissions[0].numberAttendingDinner}`);
                    setPreviousSubmissionId(submissions[0].id ?? null);
                }
            })
            .finally(() => {
                setLoading(false);
            });

        return () => {
            mounted = false;
            controller?.abort();
        };
    }, [currentUser.id]);

    /**
     * Submit handler for the annual meeting reg form.
     * 
     * @param e 
     */
    const submitContactForm = (e: React.SyntheticEvent) => {
        e.preventDefault();
        // If we're already submitting, short circuit the submit handler
        if (submitting) {
            return;
        }
        setSubmitMessage('');
        setSubmitting(true);
        const year = moment().format('YYYY');
        const title = firstName && lastName
            ? `${firstName} ${lastName} - ${year}`
            : `User ${currentUser.id} - ${year}`;

        const registrationData: AnnualMeetingRegistration = {
            firstName: firstName,
            lastName: lastName,
            spouse: spouseName,
            company: companyName,
            fpuName: fpu,
            fieldManager: fieldManager,
            contactNumber: phone,
            email: email,
            numberAttendingMeeting: attendees,
            numberAttendingDinner: attendees2,
            title: title
        };

        if (!previousSubmissionId) {
            registrationData.meetingDate = moment().startOf('year').format('YYYY-MM-DD HH:MM:SS');
        }

        const baseUrl: string = process.env.API_BASEURL ?? '';
        const url = baseUrl + ApiEndpoints.ANNUAL_MEETING_REG;

        // We'll either create or update a registration, depending on if we already have a reg id
        let upsertPromise: Promise<AnnualMeetingRegistration>|null = null;
        if (previousSubmissionId) {
            upsertPromise = axios.put(
                url + `/${previousSubmissionId}`,
                registrationData,
                {
                    withCredentials: true,
                    headers: {
                        "Accept": "application/json"
                    }
                }
            )
        } else {
            upsertPromise = axios.post(
                url,
                registrationData,
                {
                    withCredentials: true,
                    headers: {
                        "Accept": "application/json"
                    }
                }
            );
        }
        
        // Send the create or update request
        upsertPromise.then(response => {
            setSubmitMessageVariant('success');
            const closeMoment = moment(pageData.acf.annual_meeting_registration_closes).tz('America/Los_Angeles');
            setSubmitMessage(`Thank you for your registration. You can return to this page to update your information until ${closeMoment.format('dddd, MMMM Do YYYY, h:mm a z')}.`);
            setValidated('default');
            setPreviousSubmissionId(response.id ?? null);
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch(error => {
            RollbarHelper.logRequestError(error, "submitContactForm()");
            setSubmitMessageVariant('danger');
            setSubmitMessage('An error occurred while attempting to send your registration. Please try again.');
        })
        .finally(() => setSubmitting(false));
    };

    const getRegistrationSubmission = () => {
        return (
            <>
                <Title headingLevel="h2" className="pf-u-mb-md">Your Registration Data:</Title>
                <DataList aria-label="Annual Registration Data" isCompact>
                    <DataListItem aria-labelledby="annual-reg-field-1">
                        <DataListItemRow>
                            <DataListItemCells
                                dataListCells={[
                                    <DataListCell key="row-label" className="pf-u-font-weight-bold">
                                        <span id="annual-reg-field-1">First Name</span>
                                    </DataListCell>,
                                    <DataListCell key="row-value">{firstName}</DataListCell>
                                ]}
                            />
                        </DataListItemRow>
                    </DataListItem>
                    <DataListItem aria-labelledby="annual-reg-field-2">
                        <DataListItemRow>
                            <DataListItemCells
                                dataListCells={[
                                    <DataListCell key="row-label" className="pf-u-font-weight-bold">
                                        <span id="annual-reg-field-2">Last Name</span>
                                    </DataListCell>,
                                    <DataListCell key="row-value">{lastName}</DataListCell>
                                ]}
                            />
                        </DataListItemRow>
                    </DataListItem>
                    <DataListItem aria-labelledby="annual-reg-field-3">
                        <DataListItemRow>
                            <DataListItemCells
                                dataListCells={[
                                    <DataListCell key="row-label" className="pf-u-font-weight-bold">
                                        <span id="annual-reg-field-3">Spouse</span>
                                    </DataListCell>,
                                    <DataListCell key="row-value">{spouseName}</DataListCell>
                                ]}
                            />
                        </DataListItemRow>
                    </DataListItem>
                    <DataListItem aria-labelledby="annual-reg-field-4">
                        <DataListItemRow>
                            <DataListItemCells
                                dataListCells={[
                                    <DataListCell key="row-label" className="pf-u-font-weight-bold">
                                        <span id="annual-reg-field-4">Company/Dairy</span>
                                    </DataListCell>,
                                    <DataListCell key="row-value">{companyName}</DataListCell>
                                ]}
                            />
                        </DataListItemRow>
                    </DataListItem>
                    <DataListItem aria-labelledby="annual-reg-field-5">
                        <DataListItemRow>
                            <DataListItemCells
                                dataListCells={[
                                    <DataListCell key="row-label" className="pf-u-font-weight-bold">
                                        <span id="annual-reg-field-5">FPU/Producer #</span>
                                    </DataListCell>,
                                    <DataListCell key="row-value">{fpu}</DataListCell>
                                ]}
                            />
                        </DataListItemRow>
                    </DataListItem>
                    <DataListItem aria-labelledby="annual-reg-field-6">
                        <DataListItemRow>
                            <DataListItemCells
                                dataListCells={[
                                    <DataListCell key="row-label" className="pf-u-font-weight-bold">
                                        <span id="annual-reg-field-6">Field Manager</span>
                                    </DataListCell>,
                                    <DataListCell key="row-value">{fieldManager}</DataListCell>
                                ]}
                            />
                        </DataListItemRow>
                    </DataListItem>
                    <DataListItem aria-labelledby="annual-reg-field-7">
                        <DataListItemRow>
                            <DataListItemCells
                                dataListCells={[
                                    <DataListCell key="row-label" className="pf-u-font-weight-bold">
                                        <span id="annual-reg-field-7">Contact Number</span>
                                    </DataListCell>,
                                    <DataListCell key="row-value">{phone}</DataListCell>
                                ]}
                            />
                        </DataListItemRow>
                    </DataListItem>
                    <DataListItem aria-labelledby="annual-reg-field-8">
                        <DataListItemRow>
                            <DataListItemCells
                                dataListCells={[
                                    <DataListCell key="row-label" className="pf-u-font-weight-bold">
                                        <span id="annual-reg-field-8">Email</span>
                                    </DataListCell>,
                                    <DataListCell key="row-value">{email}</DataListCell>
                                ]}
                            />
                        </DataListItemRow>
                    </DataListItem>
                    <DataListItem aria-labelledby="annual-reg-field-9">
                        <DataListItemRow>
                            <DataListItemCells
                                dataListCells={[
                                    <DataListCell key="row-label" className="pf-u-font-weight-bold">
                                        <span id="annual-reg-field-9"># Attending Meeting</span>
                                    </DataListCell>,
                                    <DataListCell key="row-value">{attendees}</DataListCell>
                                ]}
                            />
                        </DataListItemRow>
                    </DataListItem>
                    <DataListItem aria-labelledby="annual-reg-field-10">
                        <DataListItemRow>
                            <DataListItemCells
                                dataListCells={[
                                    <DataListCell key="row-label" className="pf-u-font-weight-bold">
                                        <span id="annual-reg-field-10"># Attending Dinner</span>
                                    </DataListCell>,
                                    <DataListCell key="row-value">{attendees2}</DataListCell>
                                ]}
                            />
                        </DataListItemRow>
                    </DataListItem>
                </DataList>
            </>
        );
    };

    const getRegistrationForm = () => {
        const now = moment().tz('America/Los_Angeles');
        const openMoment = moment(pageData.acf.annual_meeting_registration_opens).tz('America/Los_Angeles');
        const closeMoment = moment(pageData.acf.annual_meeting_registration_closes).tz('America/Los_Angeles');
        const isOpen = now.isBetween(openMoment, closeMoment);

        // Registration is closed, show an alert and the static registration data
        if (!isOpen) {
            const isBefore = now.isBefore(openMoment);
            const alertTitle = isBefore
                ? 'Registration Opens Soon'
                : 'Registration Closed';
            const alertMessage = isBefore
                ? `Registration opens on ${openMoment.format('dddd, MMMM Do YYYY, h:mm a z')}.`
                : `Registration closed on ${closeMoment.format('dddd, MMMM Do YYYY, h:mm a z')}.`;
            return (
                <>
                    <Alert 
                        title={alertTitle}
                        variant="info" 
                        className="pf-u-mb-md"
                        role="alert"
                    >
                        {alertMessage}
                    </Alert>
                    {!isBefore &&
                        getRegistrationSubmission()
                    }
                </>
            );
        }

        // Registrations is open, and the user has submitted the form (show static reg data)
        if (submitMessageVariant === 'success') {
            return (
                <>
                    <Alert
                        title="Message Sent"
                        variant={submitMessageVariant}
                        className="pf-u-mb-md"
                        role="alert"
                    >
                        {submitMessage}
                    </Alert>
                    {getRegistrationSubmission()}
                </>
            );
        }

        // If we made it this far, show the registration form
        return (
            <Form className="pf-u-w-50-on-md" onSubmit={submitContactForm}>
                <Grid hasGutter md={6}>
                    {previousSubmissionId && !submitMessage &&
                        <GridItem span={12}>
                        <Alert
                            title="Editing Registration"
                            variant="info"
                            className="pf-u-mb-md"
                            role="alert"
                        >
                            {`Your previous registration info is listed below. You can update and resubmit until ${closeMoment.format('dddd, MMMM Do YYYY, h:mm a z')}`}.
                        </Alert>
                    </GridItem>
                    }
                    {submitMessage &&
                        <GridItem span={12}>
                            <Alert
                                title={submitMessageVariant === 'danger' ? 'Error Sending Message' : 'Message Sent' }
                                variant={submitMessageVariant}
                                className="pf-u-mb-md"
                                role="alert"
                            >
                                {submitMessage}
                            </Alert>
                        </GridItem>
                    }
                    
                    <FormGroup
                        label="First Name"
                        type="string"
                        helperTextInvalid="Please provide a first name"
                        fieldId="annual-reg-fname"
                        validated={validated}
                    >
                        <TextInput 
                            id="annual-reg-fname"
                            value={firstName}
                            onChange={value => setFirstName(value)}
                            validated={validated}
                        />
                    </FormGroup>
                    
                    <FormGroup
                        label="Last Name"
                        type="string"
                        helperTextInvalid="Please provide a last name"
                        fieldId="annual-reg-lname"
                        validated={validated}
                    >
                        <TextInput 
                            id="annual-reg-lname"
                            value={lastName}
                            onChange={value => setLastName(value)}
                            validated={validated}
                        />
                    </FormGroup>

                    <FormGroup
                        label="Spouse"
                        type="string"
                        helperTextInvalid="Please provide a spouse name"
                        fieldId="annual-reg-spouse"
                        validated={validated}
                    >
                        <TextInput 
                            id="annual-reg-spouse"
                            value={spouseName}
                            onChange={value => setSpouseName(value)}
                            validated={validated}
                        />
                    </FormGroup>

                    <FormGroup
                        label="Company/Dairy"
                        type="string"
                        helperTextInvalid="Please provide a company/dairy name"
                        fieldId="annual-reg-company"
                        validated={validated}
                    >
                        <TextInput 
                            id="annual-reg-company"
                            value={companyName}
                            onChange={value => setCompanyName(value)}
                            validated={validated}
                        />
                    </FormGroup>

                    <FormGroup
                        label="FPU/Producer #"
                        type="string"
                        helperTextInvalid="Please provide a FPU #"
                        fieldId="annual-reg-fpu"
                        validated={validated}
                    >
                        <TextInput 
                            id="annual-reg-fpu"
                            value={fpu}
                            onChange={value => setFpu(value)}
                            validated={validated}
                        />
                    </FormGroup>

                    <FormGroup
                        label="Field Manager"
                        type="string"
                        helperTextInvalid="Please provide a field manager name"
                        fieldId="annual-reg-field-manager"
                        validated={validated}
                    >
                        <TextInput 
                            id="annual-reg-field-manager"
                            value={fieldManager}
                            onChange={value => setFieldManager(value)}
                            validated={validated}
                        />
                    </FormGroup>

                    <FormGroup
                        label="Contact Number"
                        type="string"
                        helperTextInvalid="Please provide a contact number"
                        fieldId="annual-reg-number"
                        validated={validated}
                    >
                        <TextInput 
                            id="annual-reg-number"
                            type="tel"
                            value={phone}
                            onChange={value => setPhone(value)}
                            validated={validated}
                        />
                    </FormGroup>

                    <FormGroup
                        label="Email"
                        type="string"
                        helperTextInvalid="Please provide an email"
                        fieldId="annual-reg-email"
                        validated={validated}
                    >
                        <TextInput 
                            id="annual-reg-email"
                            type="email"
                            value={email}
                            onChange={value => setEmail(value)}
                            validated={validated}
                        />
                    </FormGroup>

                    <FormGroup
                        label="# Attending Meeting"
                        type="string"
                        helperTextInvalid="Please provide a number of attendees for the meeting"
                        fieldId="annual-reg-attending-meeting"
                        validated={validated}
                    >
                        <TextInput 
                            id="annual-reg-attending-meeting"
                            type="number"
                            min={0}
                            value={attendees}
                            onChange={value => setAttendees(`${Number.parseInt(value)}`)}
                            validated={validated}
                        />
                    </FormGroup>

                    <FormGroup
                        label="# Attending Dinner"
                        type="string"
                        helperTextInvalid="Please provide a number of attendees for the dinner"
                        fieldId="annual-reg-attending-dinner"
                        validated={validated}
                    >
                        <TextInput 
                            id="annual-reg-attending-dinner"
                            type="number"
                            min={0}
                            value={attendees2}
                            onChange={value => setAttendees2(`${Number.parseInt(value)}`)}
                            validated={validated}
                        />
                    </FormGroup>
                </Grid>

                <Button
                    isInline
                    type="submit"
                    variant="primary"
                >
                    Submit
                </Button>
            </Form>
        );
    };

    if (loading) {
        return <PageLoader />;
    }

    return (
        <>
            <PageSection>
                <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
                <div className="pf-c-content">
                    {htmlParser.htmlToJSX(pageData.content ?? '')}
                </div>
                {children}
            </PageSection>

            <PageSection>
                {getRegistrationForm()}
            </PageSection>
        </>
    )

};

export default AnnualMeetingRegPage;